import { useContext } from "react";
import {
  useReactFlow,
  getOutgoers,
  getConnectedEdges,
  reconnectEdge
} from "reactflow";
import { useDispatch, useSelector } from "react-redux";
import { setCopiedNode } from "redux/actions/flowDesigner/flowsActionCreator";
import { toast } from "react-toastify";
import { TabContextApi } from "layouts/Flow Designer/context";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import { NODE_TYPES, COMMON_COMPONENT_TYPES, EDGE_TYPES } from "constants/flowDesigner";
import { generateEdge } from "layouts/Flow Designer/helpers/common";
import { addErrorLog } from "Logger/logger";
import {
  addStepToContext,
  addContextVariable
} from "redux/actions/flowDesigner/editorContextActionCreator";
import useHandleStraightEdge from "hooks/useHandleStraightEdge";
import { useHandleFloatingEdge } from "hooks/useHandleFloatingEdge";
import useCreateSubFlowNode from "hooks/useCreateSubFlowNode";
import { createResourceDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";
import { extractDependenciesFromFlowConfig } from "helpers/resourceDependencies";

const STRUCTURAL_NODE_TYPES = new Set([
  NODE_TYPES.HIDDEN,
  NODE_TYPES.LABEL,
  NODE_TYPES.SUBFLOW
]);

export function useCopyPaste() {
  const reactFlowInstance = useReactFlow();
  const { getNode, getNodes, getEdges, setNodes, setEdges } = reactFlowInstance;
  const { autoSaveFlow } = useContext(TabContextApi);
  const { copiedNode } = useSelector((state) => state.designer.flows);
  const { generateNodeId, generateComponentId } = useComponentIdGenerator();
  const { currentSavedFlow, flows } = useSelector((store) => store?.designer?.flows);
  const { createStraightEdge } = useHandleStraightEdge();
  const { createFloatingEdge } = useHandleFloatingEdge();
  const { createSubFlowNode } = useCreateSubFlowNode();

  const dispatch = useDispatch();

  const createNodeDependencies = (nodes) => {
    if (!currentSavedFlow?.id || !nodes.length) return;

    const dependencies = extractDependenciesFromFlowConfig(
      currentSavedFlow.id,
      { nodes, triggers: [] }
    );

    if (dependencies.length) {
      dispatch(
        createResourceDependencies({
          tenantId: currentSavedFlow.tenant_id,
          dependencies
        })
      );
    }
  };

  const addGotoFloatingEdges = (edges, nodes) => {
    const updatedEdges = [...edges];

    nodes.forEach((node) => {
      if (node.data?.type !== COMMON_COMPONENT_TYPES.GOTO) return;

      const targetNodeId = node.data.flowElement?.data?.nodeId?.value;
      if (!targetNodeId) return;

      const floatingEdge = createFloatingEdge(node.id, targetNodeId);
      if (!updatedEdges.some((edge) => edge.id === floatingEdge.id)) {
        updatedEdges.push(floatingEdge);
      }
    });

    return updatedEdges;
  };

  const copy = (id) => {
    const selectedNode = getNodes().find((node) => node.id === id);

    if (selectedNode) {
      toast.success("Node copied successfully");
    }

    if (
      selectedNode?.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
      selectedNode?.data?.type === COMMON_COMPONENT_TYPES.SELECTION
    ) {
      dispatch(
        setCopiedNode({
          node: selectedNode,
          originalNodes: getNodes(),
          originalEdges: getEdges()
        })
      );
    } else {
      dispatch(
        setCopiedNode({
          node: selectedNode
        })
      );
    }

    // Save the flow after copying the node to save the copied node also in the flow
    autoSaveFlow(reactFlowInstance);
  };

  const getNodesAndBranches = (startNodeId, mergeId, nodes, edges) => {
    try {
      const nodesAndBranches = [];

      const traverseNodes = (nodeId) => {
        const node = nodes.find((node) => node.id === nodeId);

        if (!node) return;

        if (!nodesAndBranches.includes(node)) {
          nodesAndBranches.push(node);
        }

        const outgoing = getOutgoers(
          node,
          nodes,
          edges.filter((edge) => edge.type !== "floatingedge")
        );

        if (outgoing.length === 1 && outgoing[0].id === mergeId) {
          if (!nodesAndBranches.includes(outgoing[0])) {
            nodesAndBranches.push(outgoing[0]); // Include merge node too
          }
          return;
        }

        outgoing.forEach((outgoer) => {
          traverseNodes(outgoer.id);
        });
      };

      traverseNodes(startNodeId);

      return nodesAndBranches;
    } catch (error) {
      console.log("Error in getNodesAndBranches", error.message);
    }
  };

  // Generate new node data based on the copied node and its branches
  const generateNewNodeData = (node, newNodeId, oldToNewIds, newTargetNode, newNodesData) => {
    try {
      const baseData = {
        ...node,
        id: newNodeId,
        data: {
          ...node.data,
          id: newNodeId
        }
      };

      if (!STRUCTURAL_NODE_TYPES.has(node.type)) {
        baseData.data.componentKey = generateComponentId(node.data, newNodesData);
      } 

      if (node.data?.flowElement) {
        baseData.data.flowElement = {
          ...node.data.flowElement,
          elementId: newNodeId
        };
      }

      if (node.data?.mergeNodeParent) {
        baseData.data.mergeNodeParent = oldToNewIds[node.data?.mergeNodeParent];
      }

      if (node.data?.parentNodeId) {
        baseData.data.parentNodeId = oldToNewIds[node.data?.parentNodeId];
      }

      if (node.type === NODE_TYPES.LABEL) {
        baseData.data.labelParent = oldToNewIds[node.data?.labelParent];
        baseData.data.source = oldToNewIds[node.data?.source];
        baseData.data.target = newTargetNode.id;
      }

      return baseData;
    } catch (error) {
      console.log("Error in generateNewNodeData", error.message);
    }
  };

  // Add new nodes to the context
  const addNewNodesContext = (newNodes) => {
    if (newNodes.length === 0) return;

    newNodes.forEach((node) => {
      if (!STRUCTURAL_NODE_TYPES.has(node.type)) {
        dispatch(
          addStepToContext(
            node.data?.componentKey,
            {
              nodeId: node.id,
              type: node.type === "custom" ? "connector" : "component",
              inputData: {},
              outputData: {}
            },
            currentSavedFlow?.name
          )
        );

        if (node.data?.type === COMMON_COMPONENT_TYPES.VARIABLES) {
          dispatch(addContextVariable(node.data?.componentKey, currentSavedFlow?.name));
        }
      }
    });
  };

  const modifyNodeDataReferenceIds = (newNodesData, oldToNewIds) => {
    try {
      return newNodesData.map((node) => {
        const nodeData = node.data.flowElement?.data;

        const newNodeData = { ...nodeData };

        if (nodeData) {
          Object.keys(newNodeData).forEach((key) => {
            if (key === "nodeId") {
              newNodeData[key].value =
                oldToNewIds[newNodeData[key].value] || newNodeData[key].value;
            }

            if (
              newNodeData[key] &&
              typeof newNodeData[key].value === "string" &&
              key !== "nodeId"
            ) {
              const contextRegex = /\b(steps|variables)\.([a-zA-Z0-9_]+)\b/g;

              newNodeData[key] = { ...newNodeData[key] };

              newNodeData[key].value = newNodeData[key].value.replace(
                contextRegex,
                (match, base, nodeKey) => {
                  const oldNodeId = getNodes().find((n) => n.data?.componentKey === nodeKey)?.id;
                  const newKey = oldToNewIds[oldNodeId];

                  if (newKey) {
                    const newKeyNode = newNodesData?.find((d) => d.id === newKey);
                    const newKeyNodeComponentKey = newKeyNode?.data?.componentKey;
                    return `${base}.${newKeyNodeComponentKey}`;
                  }
                  return match;
                }
              );
            }

            if(key === "branches" && Array.isArray(newNodeData[key]) && newNodeData[key].length > 0) {
              newNodeData[key] = newNodeData[key].map((branch) => {
                const startPointNewId = oldToNewIds[branch.startPoint];
                return {
                  ...branch,
                  startPoint: startPointNewId,
                  endPoint: oldToNewIds[branch.endPoint],
                  id: oldToNewIds[branch.id],
                  key:`${startPointNewId}-${branch.value}`
                }
              });
            }
          });

          return {
            ...node,
            data: {
              ...node.data,
              flowElement: {
                ...node.data.flowElement,
                data: newNodeData
              }
            }
          };
        }

        return node;
      });
    } catch (error) {
      console.error("Error in modifyNodeDataReferenceIds", error.message);
    }
  };

  // Modify the copied nodes and branches to create new nodes with new ids and data
  const modifyNodesAndBranches = (nodesAndBranches = [], newTargetNode) => {
    try {
      let oldToNewIds = {};
      let newNodesData = [];

      if (nodesAndBranches.length) {
        nodesAndBranches.forEach((node) => {
          let newNodeId = generateNodeId();

          oldToNewIds[node.id] = newNodeId;

          const newNodeData = generateNewNodeData(
            node,
            newNodeId,
            oldToNewIds,
            newTargetNode,
            newNodesData
          );

          // if the node is a merge node, modify the parent node mergeId
          const mergeParentNodeId =
            node.type !== NODE_TYPES.SUBFLOW
              ? (node.data?.mergeNodeParent ?? node.data?.parentNodeId)
              : null;

          if (mergeParentNodeId) {
            newNodesData = newNodesData.map((newNode) => {
              if (newNode.id === oldToNewIds[mergeParentNodeId]) {
                return {
                  ...newNode,
                  data: {
                    ...newNode.data,
                    mergeId: newNodeId
                  }
                };
              }
              return newNode;
            });
          }

          newNodesData.push(newNodeData);
        });

        // modify node data references
        newNodesData = modifyNodeDataReferenceIds(newNodesData, oldToNewIds);
      }

      addNewNodesContext(newNodesData);
      return { newNodesData, oldToNewIds };
    } catch (error) {
      console.log("Error in modifyNodesAndBranches", error.message, error);
    }
  };

  // Generate new edges based on the copied nodes and branches
  const generateNewEdges = (oldToNewIds, nodesAndBranchesEdges) => {
    const newEdges = nodesAndBranchesEdges.map((edge) => {
      const newEdge = {
        ...edge,
        id: `${oldToNewIds[edge.source]}->${oldToNewIds[edge.target]}`,
        source: oldToNewIds[edge.source],
        target: oldToNewIds[edge.target]
      };
      return newEdge;
    });

    return newEdges;
  };

  // Update the isBranch property of the copied node branches based on the new source node
  const updateIsBranch = (oldToNewIds, updatedNodes, newSourceNode, cloneNode) => {
    try {
      return updatedNodes.map((node) => {
        const nodeId = cloneNode ? cloneNode.id : copiedNode.node.id;
        if (node.id === oldToNewIds[nodeId]) {
          return {
            ...node,
            data: {
              ...node.data,
              isBranch: newSourceNode?.data?.isBranch
            }
          };
        }

        return node;
      });
    } catch (error) {
      console.log("Error in updateIsBranch", error.message);
    }
  };

  const paste = (newSource, newTarget, cloneNodeData = null) => {
    const edges = getEdges();
    const controlFlowEdges = edges.filter(
      (edge) => edge.type !== EDGE_TYPES.FLOATING && edge.type !== EDGE_TYPES.STRAIGHT
    );
    let newSourceNode = getNode(newSource);
    let newTargetNode = getNode(newTarget);
    let newSourceEdge = getConnectedEdges([newTargetNode], controlFlowEdges)?.find(
      (edge) => edge.target === newTargetNode.id
    );
    let originalNodes = [];
    let originalEdges = [];
    let nodeToPaste = null;

    try {
      if (cloneNodeData) {
        originalNodes = cloneNodeData.originalNodes;
        originalEdges = cloneNodeData.originalEdges;
        nodeToPaste = cloneNodeData.node;
      } else {
        newSourceEdge = getConnectedEdges([newTargetNode], controlFlowEdges)?.find(
          (edge) => edge.target === newTargetNode.id
        );
        originalNodes = copiedNode.originalNodes || [];
        originalEdges = copiedNode.originalEdges || [];
        nodeToPaste = copiedNode.node;
      }

      if (
        nodeToPaste?.data?.mergeId &&
        (nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL ||
          nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.SELECTION)
      ) {
        const mergeNode = originalNodes.find((node) => node.id === nodeToPaste.data.mergeId);
        const nodesAndBranches = getNodesAndBranches(
          nodeToPaste.id,
          mergeNode.id,
          originalNodes,
          originalEdges
        );

        const nodesAndBranchesIds = nodesAndBranches.map((node) => node.id);
        const nodesAndBranchesEdges = originalEdges.filter((edge) => {
          return (
            nodesAndBranchesIds.includes(edge.source) && nodesAndBranchesIds.includes(edge.target)
          );
        });

        const { newNodesData, oldToNewIds } = modifyNodesAndBranches(
          nodesAndBranches,
          newTargetNode
        );
        createNodeDependencies(newNodesData);

        let updatedEdges = edges.concat(generateNewEdges(oldToNewIds, nodesAndBranchesEdges));

        updatedEdges = reconnectEdge(
          newSourceEdge,
          {
            source: newSource,
            target: oldToNewIds[nodeToPaste.id]
          },
          updatedEdges
        );

        updatedEdges = updatedEdges.concat({
          ...newSourceEdge,
          id: `${oldToNewIds[mergeNode.id]}->${newTarget}`,
          source: oldToNewIds[mergeNode.id],
          target: newTarget,
          selected: false
        });
        updatedEdges = addGotoFloatingEdges(updatedEdges, newNodesData);

        setEdges(updatedEdges);

        setNodes((prev) => {
          let updatedNodes = [...prev, ...newNodesData];
          updatedNodes = updateIsBranch(
            oldToNewIds,
            updatedNodes,
            newSourceNode,
            cloneNodeData?.node
          );
          return updatedNodes;
        });
      } else {
        const { newNodesData, oldToNewIds } = modifyNodesAndBranches([nodeToPaste], newTargetNode);
        createNodeDependencies(newNodesData);
        let updatedEdges = reconnectEdge(
          newSourceEdge,
          {
            source: newSource,
            target: oldToNewIds[nodeToPaste.id]
          },
          edges
        );
        let updatedNodes = [...getNodes(), ...newNodesData];

        updatedEdges = updatedEdges.concat(generateEdge(oldToNewIds[nodeToPaste.id], newTarget));

        updatedNodes = updateIsBranch(
          oldToNewIds,
          updatedNodes,
          newSourceNode,
          cloneNodeData?.node
        );

        if (nodeToPaste?.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
          const invokeNode = newNodesData[0];
          const subFlowData = flows?.find(
            (flow) => flow.id === invokeNode?.data?.flowElement?.data?.flowId?.value
          );
          const newSubFlowNode = createSubFlowNode(invokeNode, subFlowData);
          updatedNodes.push(newSubFlowNode);
          updatedEdges.push(createStraightEdge(invokeNode.id, newSubFlowNode.id));
        }

        updatedEdges = addGotoFloatingEdges(updatedEdges, newNodesData);

        setNodes(updatedNodes);

        setEdges(updatedEdges);
      }

      if (!cloneNodeData) {
        dispatch(setCopiedNode(null));
      }

      autoSaveFlow(reactFlowInstance, null, null, cloneNodeData ? false : true);
    } catch (error) {
      addErrorLog("Flow designer paste node", error.message);
    }
  };

  return { copy, paste };
}

export default useCopyPaste;
